import XLSX from 'xlsx';
import { formatDayLabel } from './format';

const PERIOD_HEADERS = [
    'Spend',
    'Impressions',
    'Clicks',
    'CPC',
    'Target',
    'Leads received',
    'SQL-Real',
    'Sales-TBC',
    'Total Real',
    'Sales Won',
    'Sales Won value',
    '% Achieved',
    'CPERP',
];

const DAY_SUB_HEADERS = ['Spend', 'Leads', 'SQL', 'TBC', 'Real', 'Won', 'CPERP'];

function sumSalesWonForDates(dates, getDayMetrics) {
    return (dates || []).reduce((acc, date) => {
        const day = getDayMetrics ? getDayMetrics(date) : {};
        acc.salesWon += day.salesWon || 0;
        acc.salesWonAmount += day.salesWonAmount || 0;
        return acc;
    }, { salesWon: 0, salesWonAmount: 0 });
}

function withPeriodSalesWon(row, dates, getDayMetrics) {
    if (!dates?.length || !getDayMetrics) return row;
    const won = sumSalesWonForDates(dates, getDayMetrics);
    return { ...row, salesWon: won.salesWon, salesWonAmount: won.salesWonAmount };
}

function periodCells(row, savedTarget) {
    const totalReal = row.totalReal != null ? row.totalReal : (row.leadCount || 0);
    const spend = row.spend || 0;
    const clicks = row.clicks || 0;
    const cpc = clicks > 0 ? spend / clicks : '';
    const cperp = totalReal > 0 ? spend / totalReal : '';
    let achieved = '';
    if (savedTarget != null && savedTarget > 0 && totalReal != null) {
        achieved = Math.round((totalReal / savedTarget) * 100);
    }
    return [
        spend,
        row.impressions || 0,
        clicks,
        cpc,
        savedTarget ?? '',
        row.leadCount || 0,
        row.sqlReal || 0,
        row.salesTbc || 0,
        totalReal || 0,
        row.salesWon || 0,
        row.salesWonAmount || 0,
        achieved,
        cperp,
    ];
}

function dayCells(day) {
    const d = day || {};
    const totalReal = d.totalReal != null ? d.totalReal : (d.leadCount || 0);
    const spend = d.spend || 0;
    const cperp = totalReal > 0 ? spend / totalReal : '';
    return [
        spend,
        d.leadCount || 0,
        d.sqlReal || 0,
        d.salesTbc || 0,
        totalReal || 0,
        d.salesWon || 0,
        cperp,
    ];
}

function labelHeadersForColumnCount(labelColumnCount) {
    if (labelColumnCount === 3) return ['City', 'Campaign', 'Ad set'];
    if (labelColumnCount === 2) return ['City', 'Campaign'];
    return ['Campaign'];
}

function buildHeaderRows(dates, labelColumnCount = 1) {
    const labels = labelHeadersForColumnCount(labelColumnCount);
    const row1 = [...labels, ...PERIOD_HEADERS];
    const row2 = [...labels.map(() => ''), ...PERIOD_HEADERS.map(() => '')];
    dates.forEach((date) => {
        const label = `Date · ${formatDayLabel(date)}`;
        row1.push(label, ...Array(Math.max(0, DAY_SUB_HEADERS.length - 1)).fill(''));
        row2.push(...DAY_SUB_HEADERS);
    });
    return { row1, row2 };
}

function emptyLabelCells(labelColumnCount) {
    return Array(Math.max(1, labelColumnCount)).fill('');
}

function buildMerges(dates, labelColumnCount = 1) {
    const merges = [];
    const periodColCount = labelColumnCount + PERIOD_HEADERS.length;
    for (let col = 0; col < periodColCount; col += 1) {
        merges.push({ s: { r: 0, c: col }, e: { r: 1, c: col } });
    }
    dates.forEach((date, index) => {
        const startCol = periodColCount + index * DAY_SUB_HEADERS.length;
        merges.push({
            s: { r: 0, c: startCol },
            e: { r: 0, c: startCol + DAY_SUB_HEADERS.length - 1 },
        });
    });
    return merges;
}

/**
 * @param {object} params
 * @param {string} params.from
 * @param {string} params.to
 * @param {string} params.scopeLabel
 * @param {string[]} params.dates - newest first
 * @param {Array<[string, object[]]>} params.groups
 * @param {object} params.cityDaily
 * @param {object} params.cityTargets
 * @param {object} params.adsetsState — used only to align city period totals with the UI table
 * @param {object} params.helpers - fns from CampaignSpendTable
 */
function sumScopedCityMetrics(scopedCityRows) {
    return scopedCityRows.reduce(
        (acc, row) => ({
            spend: acc.spend + (row.spend || 0),
            impressions: acc.impressions + (row.impressions || 0),
            clicks: acc.clicks + (row.clicks || 0),
            leadCount: acc.leadCount + (row.leadCount || 0),
            sqlReal: acc.sqlReal + (row.sqlReal || 0),
            salesTbc: acc.salesTbc + (row.salesTbc || 0),
            totalReal: acc.totalReal + (row.totalReal || 0),
            salesWon: acc.salesWon + (row.salesWon || 0),
            salesWonAmount: acc.salesWonAmount + (row.salesWonAmount || 0),
        }),
        {
            spend: 0,
            impressions: 0,
            clicks: 0,
            leadCount: 0,
            sqlReal: 0,
            salesTbc: 0,
            totalReal: 0,
            salesWon: 0,
            salesWonAmount: 0,
        },
    );
}

function pushDetailRowsForCity({
    aoa,
    cityName,
    cityCampaigns,
    dates,
    cityDaily,
    cityTargets,
    adsetsState,
    helpers,
}) {
    const {
        campaignRowsForCity,
        cityDayMetrics,
        sumCitySalesWon,
        metricsForCampaignInCity,
        campaignDayMetricsInCity,
        entityDayMetrics,
        matchesCityScope,
    } = helpers;

    const scopedCityRows = campaignRowsForCity(cityCampaigns, cityName, adsetsState);
    const cityMetrics = sumScopedCityMetrics(scopedCityRows);
    const cityWon = sumCitySalesWon(cityDaily, cityName, dates);
    const cityPeriodCells = periodCells(
        { ...cityMetrics, salesWon: cityWon.salesWon, salesWonAmount: cityWon.salesWonAmount },
        cityTargets[cityName],
    );
    const cityDayCells = dates.flatMap((date) => dayCells(cityDayMetrics(cityDaily, cityName, date)));

    const scopedCampaigns = cityCampaigns.filter((campaign) => matchesCityScope(campaign, cityName));
    scopedCampaigns.forEach((campaign) => {
        const detail = adsetsState[campaign.campaignId];
        const campaignName = campaign.campaignName || campaign.campaignId;
        const campaignDay = (date) => campaignDayMetricsInCity(
            cityDaily,
            campaign,
            cityName,
            detail,
            date,
        );
        const adsetRows = detail?.rows?.filter((adset) => matchesCityScope(adset, cityName)) || [];

        if (!adsetRows.length) {
            const cityCampaign = metricsForCampaignInCity(campaign, cityName, detail);
            aoa.push([
                cityName,
                campaignName,
                '',
                ...periodCells(withPeriodSalesWon(cityCampaign, dates, campaignDay), ''),
                ...dates.flatMap((date) => dayCells(campaignDay(date))),
            ]);
            return;
        }

        adsetRows.forEach((adset) => {
            const adsetDay = (date) => entityDayMetrics(cityDaily, 'adsets', adset.id, date);
            aoa.push([
                cityName,
                campaignName,
                adset.name || adset.id,
                ...periodCells(withPeriodSalesWon(adset, dates, adsetDay), ''),
                ...dates.flatMap((date) => dayCells(adsetDay(date))),
            ]);
        });
    });

    aoa.push([`Total · ${cityName}`, '', '', ...cityPeriodCells, ...cityDayCells]);
}

/**
 * @param {'city' | 'full'} [params.detailLevel] — full adds campaign + ad set rows under each city
 */
export function downloadCampaignSpendTableXlsx({
    from,
    to,
    scopeLabel,
    dates,
    groups,
    cityDaily,
    cityTargets,
    adsetsState,
    helpers,
    footerTotals,
    detailLevel = 'city',
}) {
    const { cityDayMetrics, campaignRowsForCity, sumDayAcrossCities, sumCitySalesWon } = helpers;

    const labelColumnCount = detailLevel === 'full' ? 3 : 1;
    const { row1, row2 } = buildHeaderRows(dates, labelColumnCount);
    const aoa = [row1, row2];

    groups.forEach(([cityName, cityCampaigns]) => {
        if (detailLevel === 'full') {
            pushDetailRowsForCity({
                aoa,
                cityName,
                cityCampaigns,
                dates,
                cityDaily,
                cityTargets,
                adsetsState,
                helpers,
            });
            return;
        }

        const scopedCityRows = campaignRowsForCity(cityCampaigns, cityName, adsetsState);
        const cityMetrics = sumScopedCityMetrics(scopedCityRows);
        const cityWon = sumCitySalesWon(cityDaily, cityName, dates);
        const cityRow = [
            cityName,
            ...periodCells(
                { ...cityMetrics, salesWon: cityWon.salesWon, salesWonAmount: cityWon.salesWonAmount },
                cityTargets[cityName],
            ),
            ...dates.flatMap((date) => dayCells(cityDayMetrics(cityDaily, cityName, date))),
        ];
        aoa.push(cityRow);
    });

    if (groups.length > 1 && footerTotals) {
        const footerDay = (date) => sumDayAcrossCities(cityDaily, date);
        const footerWon = sumSalesWonForDates(dates, footerDay);
        aoa.push([
            'All cities total',
            ...emptyLabelCells(labelColumnCount).slice(1),
            ...periodCells(
                { ...footerTotals, salesWon: footerWon.salesWon, salesWonAmount: footerWon.salesWonAmount },
                footerTotals.targetTotal,
            ),
            ...dates.flatMap((date) => dayCells(footerDay(date))),
        ]);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = buildMerges(dates, labelColumnCount);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Campaign breakdown');

    const scopeSlug = (scopeLabel || 'all')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'meta-ads';
    const detailSuffix = detailLevel === 'full' ? '-with-adsets' : '';
    XLSX.writeFile(wb, `meta-ads-breakdown-${scopeSlug}-${from}-to-${to}${detailSuffix}.xlsx`);
}

/**
 * Google Ads city breakdown — same sheet layout as Meta (Campaign breakdown).
 * @param {object} params
 * @param {string} params.from
 * @param {string} params.to
 * @param {string} params.scopeLabel
 * @param {string[]} params.dates - newest first
 * @param {Array<[string, object[]]>} params.groups
 * @param {(rows: object[]) => object} params.aggregateRange - period totals for a city
 * @param {(rows: object[], date: string) => object} params.sumDay - day bucket for a city
 * @param {object} [params.footerTotals] - grand total period metrics
 * @param {(date: string) => object} [params.footerDay] - grand total per day
 */
function pushGoogleCityRow({
    aoa,
    cityName,
    rows,
    dates,
    cityTargets,
    aggregateRange,
    sumDay,
}) {
    const cityDay = (date) => sumDay(rows, date);
    const metrics = aggregateRange(rows);
    aoa.push([
        cityName,
        ...periodCells(withPeriodSalesWon(metrics, dates, cityDay), cityTargets[cityName]),
        ...dates.flatMap((date) => dayCells(cityDay(date))),
    ]);
}

function pushGoogleDetailRowsForCity({
    aoa,
    cityName,
    rows,
    dates,
    cityTargets,
    aggregateRange,
    sumDay,
}) {
    const cityDay = (date) => sumDay(rows, date);
    const metrics = aggregateRange(rows);
    const cityPeriodCells = periodCells(
        withPeriodSalesWon(metrics, dates, cityDay),
        cityTargets[cityName],
    );
    const cityDayCells = dates.flatMap((date) => dayCells(cityDay(date)));

    rows.forEach((campaign) => {
        const campaignRows = [campaign];
        const campaignDay = (date) => sumDay(campaignRows, date);
        const campaignMetrics = aggregateRange(campaignRows);
        aoa.push([
            cityName,
            campaign.campaignName || campaign.campaignId,
            ...periodCells(withPeriodSalesWon(campaignMetrics, dates, campaignDay), ''),
            ...dates.flatMap((date) => dayCells(campaignDay(date))),
        ]);
    });

    aoa.push([`Total · ${cityName}`, '', ...cityPeriodCells, ...cityDayCells]);
}

/**
 * @param {'city' | 'full'} [params.detailLevel] — full adds one row per campaign under each city
 */
export function downloadGoogleCampaignSpendTableXlsx({
    from,
    to,
    scopeLabel,
    dates,
    groups,
    aggregateRange,
    sumDay,
    footerTotals,
    footerDay,
    cityTargets = {},
    detailLevel = 'city',
}) {
    const labelColumnCount = detailLevel === 'full' ? 2 : 1;
    const { row1, row2 } = buildHeaderRows(dates, labelColumnCount);
    const aoa = [row1, row2];

    groups.forEach(([cityName, rows]) => {
        if (detailLevel === 'full') {
            pushGoogleDetailRowsForCity({
                aoa,
                cityName,
                rows,
                dates,
                cityTargets,
                aggregateRange,
                sumDay,
            });
            return;
        }
        pushGoogleCityRow({
            aoa,
            cityName,
            rows,
            dates,
            cityTargets,
            aggregateRange,
            sumDay,
        });
    });

    if (footerTotals && footerDay) {
        const targetsSet = groups.filter(([name]) => cityTargets[name] != null);
        const footerTarget = targetsSet.length
            ? targetsSet.reduce((sum, [name]) => sum + (cityTargets[name] || 0), 0)
            : '';
        aoa.push([
            groups.length > 1 ? 'All cities total' : 'Grand total',
            ...emptyLabelCells(labelColumnCount).slice(1),
            ...periodCells(withPeriodSalesWon(footerTotals, dates, footerDay), footerTarget),
            ...dates.flatMap((date) => dayCells(footerDay(date))),
        ]);
    }

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!merges'] = buildMerges(dates, labelColumnCount);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Campaign breakdown');

    const scopeSlug = (scopeLabel || 'all-cities')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '') || 'google-ads';
    const detailSuffix = detailLevel === 'full' ? '-with-campaigns' : '';
    XLSX.writeFile(wb, `google-ads-breakdown-${scopeSlug}-${from}-to-${to}${detailSuffix}.xlsx`);
}
